import React, { useState, useEffect } from 'react';
import { FormField, Input } from '../ui';
import ScanService from '../../services/ScanService';

/**
 * PlatformTimeSelector - Component for selecting platforms and date range
 * 
 * Used in step 3 of the onboarding wizard to configure:
 * - Which platforms to monitor
 * - Date range for the scan
 */
export function PlatformTimeSelector({
	selectedAccount,
	platforms = [],
	dateRange,
	onPlatformsChange,
	onDateRangeChange,
}) {
	const [selectedPlatforms, setSelectedPlatforms] = useState(platforms);
	const [selectedDateRange, setSelectedDateRange] = useState(dateRange);

	// Default to the platform of the account the user just added, rather than
	// assuming X. Onboarding already knows which platform they chose.
	useEffect(() => {
		if (selectedPlatforms.length === 0) {
			const fromAccount = selectedAccount?.platform;
			const known = ScanService.getEnabledPlatforms().find(
				(p) => p.short.toLowerCase() === String(fromAccount || '').toLowerCase()
			);
			const newPlatforms = [known?.short || ScanService.getEnabledPlatforms()[0].short];
			setSelectedPlatforms(newPlatforms);
			onPlatformsChange?.(newPlatforms);
		}
		// Mount-once: seeds the selection from the account. onPlatformsChange is
		// a fresh function per parent render and must not re-seed.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Update parent when local state changes (but avoid infinite loops)
	useEffect(() => {
		if (selectedPlatforms.length > 0) {
			onPlatformsChange?.(selectedPlatforms);
		}
		// Value-only: fires when the local selection changes, not when the parent
		// re-renders and hands down a new callback identity.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedPlatforms]);

	useEffect(() => {
		if (selectedDateRange) {
			onDateRangeChange?.(selectedDateRange);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- value-only, as above
	}, [selectedDateRange]);

	// Derived, so onboarding can never offer a platform that cannot be scanned -
	// and picks up new ones automatically.
	const platformOptions = ScanService.getEnabledPlatforms().map((p) => ({
		value: p.short,
		label: p.label,
		icon: p.icon,
	}));

	const handleDateChange = (field, value) => {
		const newDateRange = { ...selectedDateRange, [field]: value };
		setSelectedDateRange(newDateRange);
	};

	// Smart date range suggestions
	const getDateRangeSuggestions = () => {
		const today = new Date();
		const suggestions = [
			{
				label: 'Last 7 days',
				value: {
					startDate: new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
					endDate: today.toISOString().split('T')[0],
				},
			},
			{
				label: 'Last 30 days',
				value: {
					startDate: new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
					endDate: today.toISOString().split('T')[0],
				},
			},
			{
				label: 'Last 90 days',
				value: {
					startDate: new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
					endDate: today.toISOString().split('T')[0],
				},
			},
		];
		return suggestions;
	};

	const handleQuickDateSelect = (dateRange) => {
		setSelectedDateRange(dateRange);
	};

	return (
		<div className="space-y-6">
			{/* Platform Selection */}
			<div>
				<h4 className="text-sm font-medium text-foreground mb-3">
					Select Platforms to Monitor
				</h4>

				{selectedAccount && (
					<div className="mb-4 p-3 bg-brand-50 border border-brand-200 rounded-md dark:bg-brand-500/10 dark:border-brand-500/30">
						<div className="text-sm text-brand-800">
							<strong>Platform:</strong> {selectedAccount.platform}
						</div>
					</div>
				)}

				<div className="grid grid-cols-1 gap-3">
					{platformOptions.map((platform) => {
			
						return (
							<div
								key={platform.value}
								className={`
									p-3 border rounded-lg text-left
									border-brand-500 bg-brand-600 text-white
								`}
							>
								<div className="flex items-center">
									<span className="text-lg mr-2">{platform.icon}</span>
									<div>
										<div className="font-medium">{platform.label}</div>
										<div className="text-xs opacity-75">Only Supported Platform</div>
									</div>
								</div>
							</div>
						);
					})}
				</div>

				{selectedPlatforms.length === 0 && (
					<div className="mt-2 text-sm text-muted-foreground">
						Choose the platforms to monitor for this account.
					</div>
				)}
			</div>

			{/* Date Range Selection */}
			<div>
				<h4 className="text-sm font-medium text-foreground mb-3">
					Select Time Period
				</h4>

				{/* Quick date range buttons */}
				<div className="mb-4">
					<div className="text-xs text-muted-foreground mb-2">Quick selections:</div>
					<div className="flex flex-wrap gap-2">
						{getDateRangeSuggestions().map((suggestion) => (
							<button
								key={suggestion.label}
								type="button"
								onClick={() => handleQuickDateSelect(suggestion.value)}
								className="px-3 py-1 text-xs border border-input rounded-md hover:bg-muted transition-colors"
							>
								{suggestion.label}
							</button>
						))}
					</div>
				</div>

				{/* Custom date range inputs */}
				<div className="grid grid-cols-2 gap-4">
					<FormField label="Start Date">
						<Input
							type="date"
							value={selectedDateRange.startDate}
							onChange={(e) => handleDateChange('startDate', e.target.value)}
							max={selectedDateRange.endDate}
						/>
					</FormField>

					<FormField label="End Date">
						<Input
							type="date"
							value={selectedDateRange.endDate}
							onChange={(e) => handleDateChange('endDate', e.target.value)}
							min={selectedDateRange.startDate}
							max={new Date().toISOString().split('T')[0]}
						/>
					</FormField>
				</div>

				{/* Date range info */}
				<div className="mt-2 text-xs text-muted-foreground">
					Selected period: {selectedDateRange.startDate} to {selectedDateRange.endDate}
				</div>
			</div>

			{/* Platform-specific guidance */}
			<div className="p-3 bg-brand-50 border border-brand-200 rounded-md dark:bg-brand-500/10 dark:border-brand-500/30">
				<div className="text-sm text-brand-800">
					<strong>Note:</strong> X (Twitter) requires daily windows for scraping due to API limitations.
				</div>
			</div>
		</div>
	);
} 